import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import {
  FileText,
  Check,
  X,
  Loader2,
  Clock,
  Building2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";

/**
 * Public, mobile-first estimate page.
 *
 * Accessed via /estimate/:token (no auth required).
 * Fetches data from the estimate-public Edge Function.
 * Accept/decline calls estimate-respond Edge Function.
 *
 * Design mandate: "Accept this estimate so we can get your project scheduled."
 */

interface LineItem {
  id: string;
  category: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  line_total: number;
}

interface PublicEstimate {
  id: string;
  estimate_number: string;
  status: string;
  title: string;
  description: string;
  notes: string;
  labor_total: number;
  material_total: number;
  other_total: number;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  estimated_days: number | null;
  valid_until: string | null;
  created_at: string;
  sent_at: string | null;
  contact_name: string;
  org_name: string;
  line_items: LineItem[];
  can_respond: boolean;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const CATEGORY_LABELS: Record<string, string> = {
  labor: "Labor",
  material: "Materials",
  other: "Other",
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";

export function PublicEstimatePage() {
  const { token } = useParams<{ token: string }>();
  const [estimate, setEstimate] = useState<PublicEstimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState<"accepted" | "declined" | null>(
    null
  );

  // Fetch estimate
  useEffect(() => {
    if (!token) {
      setError("Invalid link");
      setLoading(false);
      return;
    }

    fetch(`${SUPABASE_URL}/functions/v1/estimate-public?token=${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            body.error || `Error ${res.status}`
          );
        }
        return res.json();
      })
      .then((data: PublicEstimate) => {
        setEstimate(data);
        if (data.status === "accepted") setResponded("accepted");
        if (data.status === "declined") setResponded("declined");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleRespond = useCallback(
    async (action: "accept" | "decline") => {
      if (!token) return;
      setResponding(true);
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/estimate-respond`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token, action }),
          }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed");
        setResponded(action === "accept" ? "accepted" : "declined");
      } catch (err) {
        alert(`Failed: ${(err as Error).message}`);
      } finally {
        setResponding(false);
      }
    },
    [token]
  );

  // --- LOADING ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading estimate…</p>
        </div>
      </div>
    );
  }

  // --- ERROR ---
  if (error || !estimate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2">Estimate Not Found</h1>
          <p className="text-sm text-gray-500">
            {error || "This link may have expired or is invalid."}
          </p>
        </div>
      </div>
    );
  }

  // --- ALREADY RESPONDED ---
  if (responded) {
    const isAccepted = responded === "accepted";
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm">
          {isAccepted ? (
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          ) : (
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-bold mb-2">
            {isAccepted ? "Estimate Accepted!" : "Estimate Declined"}
          </h1>
          <p className="text-sm text-gray-500 mb-4">
            {isAccepted
              ? `Thank you! ${estimate.org_name} will be in touch to get your project scheduled.`
              : `We've let ${estimate.org_name} know. Feel free to reach out if you change your mind.`}
          </p>
          <div className="bg-white rounded-xl border p-4 text-left">
            <p className="text-xs text-gray-400 font-mono">
              {estimate.estimate_number}
            </p>
            <p className="font-semibold mt-1">{estimate.title}</p>
            <p className="text-2xl font-bold mt-2">
              {formatCurrency(Number(estimate.total))}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- ESTIMATE VIEW ---
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
              style={{ backgroundColor: "#dbeafe" }}
            >
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold">{estimate.org_name}</p>
              <p className="text-xs text-gray-500">
                Estimate for {estimate.contact_name}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Estimate header */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs text-gray-400 font-mono">
                {estimate.estimate_number}
              </p>
              <h1 className="text-xl font-bold mt-1">{estimate.title}</h1>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">
                {formatCurrency(Number(estimate.total))}
              </p>
            </div>
          </div>

          {estimate.description && (
            <p className="text-sm text-gray-600 mt-3">
              {estimate.description}
            </p>
          )}

          <div className="flex flex-wrap gap-3 mt-4 text-xs text-gray-400">
            {estimate.sent_at && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Sent {formatDate(estimate.sent_at)}
              </span>
            )}
            {estimate.valid_until && (
              <span>Valid until {formatDate(estimate.valid_until)}</span>
            )}
            {estimate.estimated_days && (
              <span>Est. {estimate.estimated_days} business days</span>
            )}
          </div>
        </div>

        {/* Line items */}
        {estimate.line_items.length > 0 && (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50">
              <h2 className="text-sm font-semibold">Breakdown</h2>
            </div>
            <div className="divide-y">
              {estimate.line_items.map((li) => (
                <div key={li.id} className="px-5 py-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{li.description}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {CATEGORY_LABELS[li.category] || li.category} ·{" "}
                        {li.quantity} {li.unit} × {formatCurrency(li.unit_price)}
                      </p>
                    </div>
                    <p className="text-sm font-semibold tabular-nums ml-4">
                      {formatCurrency(li.line_total)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="px-5 py-4 bg-gray-50 border-t space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Labor</span>
                <span className="tabular-nums">
                  {formatCurrency(Number(estimate.labor_total))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Materials</span>
                <span className="tabular-nums">
                  {formatCurrency(Number(estimate.material_total))}
                </span>
              </div>
              {Number(estimate.other_total) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Other</span>
                  <span className="tabular-nums">
                    {formatCurrency(Number(estimate.other_total))}
                  </span>
                </div>
              )}
              {Number(estimate.tax_amount) > 0 && (
                <>
                  <div className="border-t pt-1.5 flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="tabular-nums">
                      {formatCurrency(Number(estimate.subtotal))}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">
                      Tax ({(Number(estimate.tax_rate) * 100).toFixed(1)}%)
                    </span>
                    <span className="tabular-nums">
                      {formatCurrency(Number(estimate.tax_amount))}
                    </span>
                  </div>
                </>
              )}
              <div className="border-t pt-2 flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="tabular-nums">
                  {formatCurrency(Number(estimate.total))}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {estimate.notes && (
          <div className="bg-white rounded-xl border p-5">
            <h2 className="text-sm font-semibold mb-2">Notes</h2>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {estimate.notes}
            </p>
          </div>
        )}

        {/* CTA — persistent banner */}
        {estimate.can_respond && (
          <div className="sticky bottom-0 bg-white border-t pt-4 pb-6 -mx-4 px-4 space-y-3">
            <p className="text-center text-sm font-medium text-gray-700">
              Accept this estimate so we can get your project scheduled.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleRespond("decline")}
                disabled={responding}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {responding ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
                Decline
              </button>
              <button
                onClick={() => handleRespond("accept")}
                disabled={responding}
                className="flex-[2] flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50"
                style={{ minHeight: 48 }}
              >
                {responding ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
                Accept Estimate
              </button>
            </div>
          </div>
        )}

        {/* Expired state */}
        {!estimate.can_respond && estimate.status === "sent" && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-amber-800">
              This estimate has expired.
            </p>
            <p className="text-xs text-amber-600 mt-1">
              Please contact {estimate.org_name} for an updated estimate.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

PublicEstimatePage.path = "/estimate/:token";
